/**
 * Validates phone number format
 * Accepts formats like: +1234567890, (123) 456-7890, 123-456-7890, 123.456.7890, etc.
 * Rejects: alphabetic characters, invalid formats
 */

export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validates a phone number string
 * @param phone - Phone number to validate
 * @param required - Whether the field is required (default: false)
 * @returns Validation result with isValid flag and optional error message
 */
export const validatePhoneNumber = (
  phone: string,
  required: boolean = false
): PhoneValidationResult => {
  // If not required and empty, it's valid
  if (!required && (!phone || phone.trim() === '')) {
    return { isValid: true };
  }

  // If required and empty, return error
  if (required && (!phone || phone.trim() === '')) {
    return {
      isValid: false,
      error: 'Phone number is required',
    };
  }

  // Remove common formatting characters (spaces, dashes, parentheses, dots, plus sign)
  // Keep plus sign at the start if present
  const cleaned = phone.trim();
  const hasPlusPrefix = cleaned.startsWith('+');
  const withoutFormatting = cleaned.replace(/[\s\-().]/g, '');

  // Check if contains alphabetic characters
  if (/[a-zA-Z]/.test(withoutFormatting)) {
    return {
      isValid: false,
      error: 'Phone number cannot contain letters',
    };
  }

  // Remove plus sign for length check
  const digitsOnly = hasPlusPrefix ? withoutFormatting.slice(1) : withoutFormatting;

  // Check if contains only digits (and optional leading +)
  if (!/^\+?\d+$/.test(withoutFormatting)) {
    return {
      isValid: false,
      error: 'Phone number can only contain numbers and formatting characters',
    };
  }

  // Validate length (minimum 7 digits, maximum 15 digits for international format)
  // E.164 format allows up to 15 digits (excluding the +)
  if (digitsOnly.length < 7) {
    return {
      isValid: false,
      error: 'Phone number is too short (minimum 7 digits)',
    };
  }

  if (digitsOnly.length > 15) {
    return {
      isValid: false,
      error: 'Phone number is too long (maximum 15 digits)',
    };
  }

  return { isValid: true };
};

/**
 * Formats phone number for display (optional helper)
 * @param phone - Phone number to format
 * @returns Formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned;
};
